import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { useQuery } from "@tanstack/react-query";

export type Currency = "USD" | "INR";

export type PricingInfo = {
  currency: Currency;
  country: string | null;
  // Submission (one-time)
  submissionAmountMinor: number; // cents or paise
  submissionAmountDisplay: string; // "$25" | "₹1,999"
  submissionApprox: string | null; // "(~$25)" or null
  // Extension Pro (monthly)
  proMonthlyDisplay: string; // "$25/mo" | "₹1,999/mo"
};

const USD_PRICING: PricingInfo = {
  currency: "USD",
  country: null,
  submissionAmountMinor: 2500,
  submissionAmountDisplay: "$25",
  submissionApprox: null,
  proMonthlyDisplay: "$15/mo",
};

const INR_PRICING: PricingInfo = {
  currency: "INR",
  country: "IN",
  submissionAmountMinor: 199900,
  submissionAmountDisplay: "₹1,999",
  submissionApprox: "(~$25)",
  proMonthlyDisplay: "₹990/mo",
};

/**
 * Server-side pricing derivation from Cloudflare's CF-IPCountry header.
 * IN => INR, everything else (including unknown) => USD.
 * Exported for reuse by createCheckout so the charge currency is never
 * client-controlled.
 */
export function derivePricingFromCountry(country: string | null | undefined): PricingInfo {
  const c = (country || "").toUpperCase();
  if (c === "IN") return INR_PRICING;
  return USD_PRICING;
}

export const getPricing = createServerFn({ method: "GET" }).handler(async (): Promise<PricingInfo> => {
  try {
    const country = getRequestHeader("cf-ipcountry") ?? null;
    return derivePricingFromCountry(country);
  } catch {
    return USD_PRICING;
  }
});

/** Client hook. Returns USD pricing as a safe default until the server responds. */
export function usePricing(): PricingInfo {
  const { data } = useQuery({
    queryKey: ["pricing"],
    queryFn: () => getPricing(),
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return data ?? USD_PRICING;
}
